import { Component, ChangeDetectionStrategy, HostListener, Injector } from '@angular/core';
import { Observable, Subscription, catchError, filter, interval, map, of, switchMap, take } from 'rxjs';
import { PagedObjectManagerComponent } from '../../../core/component/paged-object-manager/paged-object-manager';
import { FilterDefinitionItem } from '../../../core/model/filter-definition-item';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { tenantCommand } from '../../model/const/command';
import { Contact } from '../../model/contact';
import { PrintGdprCommandHandler } from '../../service/command/print-gdpr-command-handler.service';
import { SendRegistrationKeyCommandHandler } from '../../service/command/send-registration-key-command-handler.service';
import { TenantCommandHandlerResolver } from '../../service/command/tenant-command-handler-resolver.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { ContactService } from '../../service/data/contact.service';
import { UserAccountService } from '../../../common/service/user-account.service';
import { Command, CommandBase, standardCommand } from '../../../core/model/command';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { ProtectedIdentityAccess } from '../../model/protected-identity';
import { ProtectedIdentityService } from '../../service/data/protected-identity.service';
import { ProtectedIdentityBankIdDialogComponent } from '../protected-identity-bankid-dialog/protected-identity-bankid-dialog.component';
import { TenantCategory } from '../../model/const/enums';

@Component({
    selector: 'app-contact-manager',
    templateUrl: './contact-manager.component.html',
    styleUrls: [
      './../../../core/component/paged-object-manager/paged-object-manager.scss',
      './contact-manager.component.scss',
    ],
    providers: [TenantCommandHandlerResolver, PrintGdprCommandHandler, SendRegistrationKeyCommandHandler],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ContactManagerComponent extends PagedObjectManagerComponent<Contact> {
  hasProtectedIdentityAccessSession = false;
  secondsRemaining = 0;

  private accessExpiresAt = 0;
  private accessTimerSubscription?: Subscription;
  private hasProtectedIdentityFeature = false;
  private isCheckingAccess = false;

  constructor(
    columnDefinitionService: ColumnDefinitionService,
    contactDataService: ContactService,
    dispatcher: CommandDispatcher,
    handlerResolver: TenantCommandHandlerResolver,
    messageService: MessageService,
    navigationService: NavigationService,
    private _userAccount: UserAccountService,
    private readonly actionWindowService: ActionWindowService,
    private readonly injector: Injector,
    private readonly protectedIdentityService: ProtectedIdentityService
  ) {
    super(contactDataService, messageService, navigationService, dispatcher, handlerResolver);

    this.menuDefinition = [new MenuItemDefinition('COMMON.EXCEL_EXPORT', tenantCommand.export)];
    this.preferencesKey = 'rent.contact.list';

    this.AddSubscription(
      this._userAccount.current.subscribe({
        next: account => {
          this.hasProtectedIdentityFeature = account.hasProtectedIdentityAccess();
          if (account.hasContactWriteAccess()) {
            this.allowNew = true;
          } else {
            this.allowNew = false;
          }
          if (account.hasContactWriteAccess()) {
            this.addContextMenuItemDefinition('TENANT.COMMAND.SEND_REGISTRATION_KEY', tenantCommand.sendRegistrationKey);
          }
          if (account.hasContactWriteAccess()) {
            this.addContextMenuItemDefinition('TENANT.COMMAND.CREATE_FAULT_NOTIFICATION', tenantCommand.showFaultNotification);
          }
          this.addContextMenuItemDefinition('TENANT.COMMAND.PRINT_GDPR', tenantCommand.printGDPR);
          if (account.hasContactWriteAccess()) {
            this.addContextMenuItemDefinition('TENANT.COMMAND.CLEANUP_DATA', tenantCommand.cleanupData);
          }
          this.addContextMenuItemDefinition('TENANT.COMMAND.SHOW_INVOICE', tenantCommand.showInvoices);
          this.syncProtectedIdentityContextMenu();
          if (account.hasContactWriteAccess()) {
            this.addContextMenuItemDefinition('TENANT.COMMAND.REPAY_BALANCE', tenantCommand.repayBalance);
          }
          if (account.hasContactWriteAccess()) {
            this.addContextMenuItemDefinition('TENANT.COMMAND.MOVE_BALANCE', tenantCommand.moveBalance);
          }
        },
      })
    );

    this.columnDefinition = columnDefinitionService.create('Tenant');

    this.title = 'TENANT.LIST_HEADER';
  }

  ngOnInit() {
    this.AddSubscription(
      this._userAccount.current
        .pipe(
          take(1),
          switchMap(account =>
            (<ContactService>this.dataService).getFilterDefinition().pipe(
              switchMap(definition =>
                (account.hasProtectedIdentityAccess()
                  ? this.protectedIdentityService.getAccess(false)
                  : of({ hasAccess: false, sessionSecondsRemaining: 0 })
                ).pipe(
                  catchError(() => of({ hasAccess: false, sessionSecondsRemaining: 0 })),
                  map(access => ({ definition, account, access }))
                )
              )
            )
          )
        )
        .subscribe(({ definition, account, access }) => {
          this.defaultFilters = [...definition.defaultFields];
          this.filterDefinition = [
          new FilterDefinitionItem(
            'TENANT.CONTACT_NUMBER',
            'number',
            definition.number,
            null,
            definition.selectedFields.includes('number')
          ),
          new FilterDefinitionItem('TENANT.NAME', 'name', definition.name, null, definition.selectedFields.includes('name')),
          new FilterDefinitionItem('TENANT.EMAIL', 'email', definition.email, null, definition.selectedFields.includes('email')),
          new FilterDefinitionItem('TENANT.PHONE', 'phone', definition.phone, null, definition.selectedFields.includes('phone')),
          new FilterDefinitionItem(
            'TENANT.CIVIC_ORG_NUMBER',
            'contactIdentification',
            definition.contactIdentification,
            null,
            definition.selectedFields.includes('contactIdentification')
          ),
          new FilterDefinitionItem(
            'TENANT.CATEGORY',
            'category',
            definition.category,
            [
              { caption: 'ALL_TEXT', value: null },
              { caption: 'CONST.TENANT_CATEGORY_PHYSICAL_PERSON', value: TenantCategory.PhysicalPerson },
              { caption: 'CONST.TENANT_CATEGORY_COMPANY', value: TenantCategory.Company },
            ],
            definition.selectedFields.includes('category')
          ),
        ];

          if (this.hasProtectedIdentityFeature) {
            this.filterDefinition.push(
              new FilterDefinitionItem(
                'PROTECTED_IDENTITY.FILTER',
                'protectedIdentityStatus',
                access.hasAccess ? definition.protectedIdentityStatus : null,
                [
                  { caption: 'ALL_TEXT', value: '' },
                  { caption: 'YES_TEXT', value: true },
                  { caption: 'NO_TEXT', value: false },
                ],
                definition.selectedFields.includes('protectedIdentityStatus')
              )
            );
          }

          if (this.hasProtectedIdentityFeature && access.hasAccess) this.startAccessTimer(access.sessionSecondsRemaining);
        })
    );

    this.AddSubscription(
      this._userAccount.current.subscribe({
        next: account => {
          if (account.hasContactWriteAccess()) {
            this.addContextMenuItemDefinition('EDIT_BTN', standardCommand.edit);
            this.addContextMenuItemDefinition('DELETE_BTN', standardCommand.delete);
          } else {
            this.addContextMenuItemDefinition('EDIT_BTN', standardCommand.edit);
          }
        },
      })
    );
  }

  get formattedAccessTime(): string {
    const minutes = Math.floor(this.secondsRemaining / 60);
    const seconds = this.secondsRemaining % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  override onFilterChange(filterState: any): void {
    const normalizedFilter = {
      ...filterState,
      category: this.toCategoryFilterValue(filterState.category),
      protectedIdentityStatus: this.toProtectedIdentityFilterValue(filterState.protectedIdentityStatus),
    };

    if (normalizedFilter.protectedIdentityStatus === null) {
      super.onFilterChange(normalizedFilter);
      return;
    }

    let accessGranted = false;
    this.AddSubscription(
      this.requestProtectedIdentityAccess()
        .pipe(take(1), filter(access => access?.hasAccess === true))
        .subscribe({
          next: access => {
            accessGranted = true;
            this.startAccessTimer(access.sessionSecondsRemaining);
            super.onFilterChange(normalizedFilter);
          },
          complete: () => {
            if (!accessGranted) this.resetProtectedIdentityFilter();
          },
          error: () => this.resetProtectedIdentityFilter(),
        })
    );
  }

  override canExecute(command: CommandBase): boolean {
    if (command instanceof Command && command.id === tenantCommand.protectedIdentity) {
      return (
        this.hasProtectedIdentityFeature &&
        !!command.context?.id &&
        command.context.category === TenantCategory.PhysicalPerson
      );
    }

    return super.canExecute(command);
  }

  override execute(command: CommandBase): void {
    if (command instanceof Command && command.id === tenantCommand.protectedIdentity) {
      this.openProtectedIdentityContact(command.context);
      return;
    }

    super.execute(command);
  }

  extendProtectedIdentityAccess(): void {
    if (!this.hasProtectedIdentityAccessSession || this.secondsRemaining === 0) return;

    this.AddSubscription(
      this.protectedIdentityService.extendAccess().subscribe({
        next: access => this.startAccessTimer(access.sessionSecondsRemaining),
        error: () => this.clearAccessSession(),
      })
    );
  }

  endProtectedIdentityAccess(): void {
    this.AddSubscription(
      this.protectedIdentityService.endAccess().subscribe({
        next: () => this.clearAccessSession(),
        error: () => this.clearAccessSession(),
      })
    );
  }

  @HostListener('window:focus')
  onWindowFocus(): void {
    this.refreshAccessState();
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.visibilityState === 'visible') this.refreshAccessState();
  }

  override ngOnDestroy(): void {
    this.stopAccessTimer();
    super.ngOnDestroy();
  }

  private openProtectedIdentityContact(contact: Contact): void {
    if (!contact?.id || !this.hasProtectedIdentityFeature || contact.category !== TenantCategory.PhysicalPerson) return;

    this.AddSubscription(
      this.requestProtectedIdentityAccess()
        .pipe(take(1), filter(access => access?.hasAccess === true))
        .subscribe({
          next: access => {
            this.startAccessTimer(access.sessionSecondsRemaining);
            this.navigation.navigateToForm(contact.id, false, false, {
              queryParams: { openProtectedIdentity: true },
            });
          },
          error: () => this.clearAccessSession(),
        })
    );
  }

  private requestProtectedIdentityAccess(): Observable<ProtectedIdentityAccess> {
    return this.protectedIdentityService.getAccess().pipe(
      switchMap(access =>
        access.hasAccess
          ? of(access)
          : this.actionWindowService.open(ProtectedIdentityBankIdDialogComponent, this.injector)
      )
    );
  }

  private startAccessTimer(seconds: number): void {
    this.stopAccessTimer();
    this.hasProtectedIdentityAccessSession = seconds > 0;
    this.accessExpiresAt = Date.now() + Math.max(0, seconds) * 1000;
    this.updateAccessTimer();
    if (!this.hasProtectedIdentityAccessSession) return;

    this.accessTimerSubscription = interval(1000).subscribe(() => this.updateAccessTimer());
  }

  private updateAccessTimer(): void {
    this.secondsRemaining = Math.max(0, Math.ceil((this.accessExpiresAt - Date.now()) / 1000));
    if (this.secondsRemaining > 0) return;

    this.clearAccessSession();
  }

  private clearAccessSession(): void {
    this.stopAccessTimer();
    this.hasProtectedIdentityAccessSession = false;
    this.resetProtectedIdentityFilter();
  }

  private refreshAccessState(): void {
    if (!this.hasProtectedIdentityAccessSession || this.isCheckingAccess) return;

    this.isCheckingAccess = true;
    this.AddSubscription(
      this.protectedIdentityService.getAccess(false).subscribe({
        next: access => {
          this.isCheckingAccess = false;
          if (access.hasAccess) {
            this.startAccessTimer(access.sessionSecondsRemaining);
            return;
          }
          this.clearAccessSession();
        },
        error: () => {
          this.isCheckingAccess = false;
          this.clearAccessSession();
        },
      })
    );
  }

  private stopAccessTimer(): void {
    this.accessTimerSubscription?.unsubscribe();
    this.accessTimerSubscription = undefined;
    this.accessExpiresAt = 0;
    this.secondsRemaining = 0;
  }

  private resetProtectedIdentityFilter(): void {
    const control = this.filterComponent?.form?.get('protectedIdentityStatus');
    if (!control) return;

    if (this.toProtectedIdentityFilterValue(control.value) !== null) {
      this.itemsSource = [];
      this.totalResults = 0;
    }

    control.setValue('');
    control.markAsDirty();
    control.updateValueAndValidity({ emitEvent: false });
    this.cdr.detectChanges();
  }

  private toProtectedIdentityFilterValue(value: unknown): boolean | null {
    if (value === true || value === 'true') return true;
    if (value === false || value === 'false') return false;
    return null;
  }

  private toCategoryFilterValue(value: unknown): TenantCategory | null {
    if (value === TenantCategory.PhysicalPerson || value === '0') return TenantCategory.PhysicalPerson;
    if (value === TenantCategory.Company || value === '1') return TenantCategory.Company;
    return null;
  }

  private syncProtectedIdentityContextMenu(): void {
    this.contextMenuDefinition = this.contextMenuDefinition.filter(item => item.commandId !== tenantCommand.protectedIdentity);
    if (this.hasProtectedIdentityFeature) {
      this.addContextMenuItemDefinition(
        'PROTECTED_IDENTITY.MENU_ITEM',
        tenantCommand.protectedIdentity,
        contact => contact?.category !== TenantCategory.PhysicalPerson
      );
      return;
    }

    this.removeProtectedIdentityFilter();
  }

  private removeProtectedIdentityFilter(): void {
    const filterComponent = this.filterComponent;
    const hadActiveFilter = filterComponent?.form?.contains('protectedIdentityStatus') === true;

    filterComponent?.removeFilter('protectedIdentityStatus');
    this.filterDefinition = this.filterDefinition?.filter(item => item.name !== 'protectedIdentityStatus');
    this.defaultFilters = this.defaultFilters?.filter(name => name !== 'protectedIdentityStatus');
    this.stopAccessTimer();
    this.hasProtectedIdentityAccessSession = false;

    if (!hadActiveFilter) return;

    super.onFilterChange({
      ...filterComponent.form.value,
      protectedIdentityStatus: null,
      selectedFields: filterComponent.filters
        .filter(item => item.selected && item.name !== 'protectedIdentityStatus')
        .map(item => item.name),
    });
  }
}
